/**
 * Communication Trace gateway (ODE-P10-T07) — thin HTTP client over the
 * ODE-P10-T06 versioned read endpoints.
 *
 *   GET  /v1/communication-trace/summaries            summary
 *   GET  /v1/communication-trace/contacts/:contactKey contact detail
 *   GET  /v1/communication-trace/search               search (purpose/case + audit)
 *   POST /v1/communication-trace/export               export (purpose/case + audit)
 *
 * Path convention matches the shell ApiClient (apps/ode-admin-web/src/api/
 * client.ts): `baseUrl` defaults to `/v1` (reverse proxy) and paths are
 * relative to it, so the full URL is `/v1/communication-trace/...`.
 *
 * The gateway is a port: the dashboard consumes `CommunicationTraceGateway`
 * and tests inject a hermetic fake. The HTTP adapter sends the caller-supplied
 * trace access token (T06 session plane) as `Authorization: Bearer`; the T06
 * backend remains the authorization authority (401/403/503 fail-closed).
 * Non-2xx responses are mapped to typed outcomes — the gateway never throws
 * for HTTP outcomes and never fabricates success.
 *
 * Traces to: ODE-PRD-REQ-098, ODE-PRD-REQ-099, ODE-PRD-REQ-100,
 *            ODE-PRD-REQ-101, ODE-PRD-REQ-110.
 */

import type {
  CommunicationTraceSummary,
  ContactDetail,
  ExportBundle,
  PurposeCode,
  SearchResults,
  WindowLabel,
} from './types.js';

export interface SummaryQuery {
  readonly driverId: string;
  readonly window: WindowLabel;
  readonly start?: string;
  readonly end?: string;
}

export interface DetailQuery extends SummaryQuery {
  readonly contactKey: string;
}

export interface SearchQuery extends SummaryQuery {
  readonly q: string;
  readonly purposeCode: PurposeCode;
  readonly caseRef?: string;
}

export interface ExportRequest {
  readonly driverId: string;
  readonly window: WindowLabel;
  readonly start?: string;
  readonly end?: string;
  readonly purposeCode: PurposeCode;
  readonly caseRef?: string;
}

/** Typed gateway outcome — never throws for HTTP outcomes. */
export type TraceOutcome<T> =
  | { readonly kind: 'ok'; readonly status: 200; readonly payload: T }
  | { readonly kind: 'error'; readonly status: number; readonly code: string; readonly message: string };

export interface CommunicationTraceGateway {
  fetchSummary(query: SummaryQuery, token: string): Promise<TraceOutcome<CommunicationTraceSummary>>;
  fetchContactDetail(query: DetailQuery, token: string): Promise<TraceOutcome<ContactDetail>>;
  search(query: SearchQuery, token: string): Promise<TraceOutcome<SearchResults>>;
  exportBundle(request: ExportRequest, token: string): Promise<TraceOutcome<ExportBundle>>;
}

interface ErrorEnvelope {
  readonly success: false;
  readonly error: { readonly code: string; readonly message: string };
  readonly requestId: string;
  readonly timestamp: string;
}

const DEFAULT_TIMEOUT_MS = 10_000;

function buildQueryString(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const encoded = search.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

export class HttpCommunicationTraceGateway implements CommunicationTraceGateway {
  constructor(
    private readonly baseUrl: string,
    // NOTE: never default to the bare `fetch` reference — calling it as a method of this
    // instance makes the browser reject it with "Illegal invocation" before any network
    // I/O (see api/client.fetch-bind.test.ts). Bind it to the global receiver instead.
    private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init),
    private readonly timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ) {}

  private async request<T>(path: string, init: RequestInit, token: string): Promise<TraceOutcome<T>> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        signal: controller.signal,
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
          ...(init.headers ?? {}),
        },
      });
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const envelope = body as ErrorEnvelope | null;
        return {
          kind: 'error',
          status: response.status,
          code: envelope?.error?.code ?? 'UNKNOWN',
          message: envelope?.error?.message ?? `HTTP ${response.status}`,
        };
      }
      return { kind: 'ok', status: 200, payload: body as T };
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === 'AbortError';
      return {
        kind: 'error',
        status: 0,
        code: aborted ? 'TIMEOUT' : 'NETWORK',
        message: aborted ? 'Permintaan melewati batas waktu.' : 'Gagal terhubung ke server.',
      };
    } finally {
      clearTimeout(timer);
    }
  }

  fetchSummary(query: SummaryQuery, token: string): Promise<TraceOutcome<CommunicationTraceSummary>> {
    const qs = buildQueryString({
      driverId: query.driverId,
      window: query.window,
      start: query.start,
      end: query.end,
    });
    return this.request<CommunicationTraceSummary>(`/communication-trace/summaries${qs}`, { method: 'GET' }, token);
  }

  fetchContactDetail(query: DetailQuery, token: string): Promise<TraceOutcome<ContactDetail>> {
    const qs = buildQueryString({
      driverId: query.driverId,
      window: query.window,
      start: query.start,
      end: query.end,
    });
    return this.request<ContactDetail>(
      `/communication-trace/contacts/${encodeURIComponent(query.contactKey)}${qs}`,
      { method: 'GET' },
      token,
    );
  }

  search(query: SearchQuery, token: string): Promise<TraceOutcome<SearchResults>> {
    const qs = buildQueryString({
      driverId: query.driverId,
      window: query.window,
      start: query.start,
      end: query.end,
      q: query.q,
      purposeCode: query.purposeCode,
      caseRef: query.caseRef,
    });
    return this.request<SearchResults>(`/communication-trace/search${qs}`, { method: 'GET' }, token);
  }

  exportBundle(request: ExportRequest, token: string): Promise<TraceOutcome<ExportBundle>> {
    return this.request<ExportBundle>(
      '/communication-trace/export',
      {
        method: 'POST',
        body: JSON.stringify({
          driverId: request.driverId,
          window: request.window,
          start: request.start,
          end: request.end,
          purposeCode: request.purposeCode,
          caseRef: request.caseRef,
        }),
      },
      token,
    );
  }
}

/** Default client: same-origin `/v1` (reverse proxy) unless VITE_API_BASE_URL is set. */
export const traceGateway: CommunicationTraceGateway = new HttpCommunicationTraceGateway(
  import.meta.env.VITE_API_BASE_URL ?? '/v1',
);